import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { DillButton } from '../Dill/DillButton';
import { DillModal } from '../Dill/DillModal';
import { AppInputField } from '../AppInputField/AppInputField';
import { X } from 'react-feather';
import AppFilePicker from '../AppFilePicker/AppFilePicker';
import { useAppSelector } from '../../../redux/store';

export interface StatementBuyerUser {
  email: string;
  firstName?: string;
  lastName?: string;
  userId: string;
  isStatementsEmailOptOut: boolean;
}

type SendMode = 'automatic' | 'customize';

interface AppSendStatementModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (data: {
    excludedUserIds: string[];
    additionalEmails: string[];
    subjectLine?: string;
    notes?: string;
    attachments?: File[];
  }) => void;
  loading?: boolean;
  buyerUsers?: StatementBuyerUser[];
}

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB in bytes

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const AppSendStatementModal: React.FC<AppSendStatementModalProps> = ({
  open,
  handleClose,
  onSubmit,
  loading = false,
  buyerUsers,
}) => {
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const prevOpenRef = useRef(false);
  const [mode, setMode] = useState<SendMode>('automatic');
  const [excludedUserIds, setExcludedUserIds] = useState<string[]>([]);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [subjectLine, setSubjectLine] = useState('');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [fileSizeError, setFileSizeError] = useState('');

  const getDefaultSubjectLine = () =>
    `Your ${currentSupplier?.name || ''} Statement for ${moment(new Date()).format('MMMM YYYY')}`;

  const resetState = () => {
    setMode('automatic');
    setExcludedUserIds([]);
    setAdditionalEmails([]);
    setNewEmailInput('');
    setEmailInputError('');
    setSubjectLine(getDefaultSubjectLine());
    setNotes('');
    setAttachments([]);
    setFileSizeError('');
  };

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open && !wasOpen) {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const defaultRecipients = buyerUsers ?? [];
  const activeDefaultRecipients = defaultRecipients.filter(
    (u) => !excludedUserIds.includes(u.userId)
  );
  const totalRecipients = activeDefaultRecipients.length + additionalEmails.length;

  const handleAddEmail = () => {
    const trimmed = newEmailInput.trim();
    if (!isValidEmail(trimmed)) {
      setEmailInputError('Please enter a valid email address.');
      return;
    }
    const alreadyDefault = defaultRecipients.some(
      (u) => u.email.toLowerCase() === trimmed.toLowerCase()
    );
    const alreadyAdded = additionalEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase());
    if (alreadyDefault || alreadyAdded) {
      setEmailInputError('This email is already in the recipient list.');
      return;
    }
    setAdditionalEmails((prev) => [...prev, trimmed]);
    setNewEmailInput('');
    setEmailInputError('');
  };

  const handleFileSelect = (files: File[]) => {
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((f) => f.name).join(', ');
      setFileSizeError(
        `The following file(s) exceed the 3MB limit: ${fileNames}. Please select smaller files.`
      );
      setAttachments([]);
    } else {
      setFileSizeError('');
      setAttachments(files);
    }
  };

  const handleSubmit = () => {
    const trimmed = newEmailInput.trim();
    if (trimmed) {
      if (!isValidEmail(trimmed)) {
        setEmailInputError('Please enter a valid email address.');
        return;
      }
      const alreadyExists =
        activeDefaultRecipients.some((u) => u.email.toLowerCase() === trimmed.toLowerCase()) ||
        additionalEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase());
      if (alreadyExists) {
        setEmailInputError('This email is already in the recipient list.');
        return;
      }
    }
    const finalEmails = trimmed ? [...additionalEmails, trimmed] : additionalEmails;
    onSubmit({
      excludedUserIds,
      additionalEmails: finalEmails,
      ...(mode === 'customize' ? { subjectLine, notes, attachments } : {}),
    });
  };

  const handleCloseModal = () => {
    resetState();
    handleClose();
  };

  const pendingEmailIsValid = !!newEmailInput.trim() && isValidEmail(newEmailInput.trim());
  const isSendDisabled =
    loading ||
    (totalRecipients === 0 && !pendingEmailIsValid) ||
    !!fileSizeError ||
    (mode === 'customize' && !subjectLine.trim());

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Send Statement"
      maxWidth="min(90vw, 640px)"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
          <DillButton
            label="Send Statement"
            onClick={handleSubmit}
            isDisabled={isSendDisabled}
            isLoading={loading}
          />
        </>
      }>
      <div className="space-y-5">
        {/* Send mode — automatic vs customize */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Send Type
          </p>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="statementSendMode"
                value="automatic"
                checked={mode === 'automatic'}
                onChange={() => setMode('automatic')}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Automatic Send</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="statementSendMode"
                value="customize"
                checked={mode === 'customize'}
                onChange={() => setMode('customize')}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Customize Send</span>
            </label>
          </div>
        </div>

        {/* Recipients */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Recipients
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Automatically populated emails from your customer's users, but you can exclude any
              emails or add additional ones before sending.
            </p>
          </div>

          {(activeDefaultRecipients.length > 0 || additionalEmails.length > 0) && (
            <div className="flex flex-wrap gap-1.5">
              {activeDefaultRecipients.map((u) => (
                <span
                  key={u.userId}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-700 shadow-sm">
                  {u.email}
                  <button
                    type="button"
                    onClick={() => setExcludedUserIds((prev) => [...prev, u.userId])}
                    className="ml-0.5 text-gray-400 hover:text-gray-700 cursor-pointer bg-transparent border-none leading-none">
                    <X size={11} />
                  </button>
                </span>
              ))}
              {additionalEmails.map((email, i) => (
                <span
                  key={`${email}-${i}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700">
                  {email}
                  <button
                    type="button"
                    onClick={() => setAdditionalEmails((prev) => prev.filter((e) => e !== email))}
                    className="ml-0.5 text-blue-400 hover:text-blue-700 cursor-pointer bg-transparent border-none leading-none">
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {totalRecipients === 0 && !pendingEmailIsValid && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              No recipients selected — add at least one email to send.
            </p>
          )}

          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <AppInputField
                placeholder="Add email address"
                value={newEmailInput}
                onTextChange={(text) => {
                  setNewEmailInput(text);
                  if (emailInputError) setEmailInputError('');
                }}
              />
              {emailInputError && <p className="text-xs text-red-600 mt-1">{emailInputError}</p>}
            </div>
            <DillButton
              label="Add"
              variant="secondary"
              onClick={handleAddEmail}
              className="mt-0.5 whitespace-nowrap"
            />
          </div>
        </div>

        {/* Customization fields */}
        {mode === 'customize' && (
          <>
            <AppInputField
              label="Subject Line"
              placeholder="Enter email subject"
              value={subjectLine}
              onTextChange={(text) => setSubjectLine(text)}
              isRequired
            />

            <AppInputField
              label="Additional Notes"
              placeholder="Add any additional notes to include in the email body"
              inputType="textarea"
              value={notes}
              onTextChange={(text) => setNotes(text)}
            />

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Attachments{' '}
                <span className="normal-case font-normal text-gray-400">(optional)</span>
              </p>
              <AppFilePicker
                onSelectFile={handleFileSelect}
                onDropProps={{
                  accept: {
                    'application/pdf': ['.pdf'],
                    'image/*': ['.png', '.jpg', '.jpeg'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                      '.docx',
                    ],
                  },
                }}
              />
              {fileSizeError && (
                <p className="text-xs text-red-600 mt-2" role="alert">
                  {fileSizeError}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1.5">Maximum file size: 3MB per file</p>
            </div>
          </>
        )}
      </div>
    </DillModal>
  );
};

export default AppSendStatementModal;
